import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipForward, X, Radio } from "lucide-react";
import { useRadio } from "@/context/RadioContext";
import { useLang } from "@/context/LanguageContext";
import ShareTrack from "@/components/ShareTrack";
import ListenFull from "@/components/ListenFull";

const SLIDE_UP = { initial: { y: 90, opacity: 0 }, animate: { y: 0, opacity: 1 }, exit: { y: 90, opacity: 0 }, transition: { type: "spring", stiffness: 260, damping: 26 } };

export default function MiniPlayer() {
  const radio = useRadio();
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const current = radio?.current;
  const show = Boolean(current) && pathname !== "/radio";

  useEffect(() => {
    document.body.style.paddingBottom = show ? "84px" : "";
    return () => { document.body.style.paddingBottom = ""; };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          {...SLIDE_UP}
          className="fixed bottom-[4.25rem] md:bottom-3 inset-x-3 sm:inset-x-auto sm:start-1/2 sm:-translate-x-1/2 rtl:sm:translate-x-1/2 sm:w-[560px] z-40 print:hidden"
          data-testid="mini-player"
        >
          <div className="relative rounded-2xl bg-ink text-sand shadow-2xl border border-sand/10 overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-0.5 bg-sand/15">
              <div className="h-full bg-ochre transition-[width] duration-200" style={{ width: `${radio.progress}%` }} />
            </div>
            <div className="flex items-center gap-3 p-2.5 pe-3">
              <Link to="/radio" className="relative w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-sandDeep/30 group" aria-label={t("radio_open")} data-testid="mini-player-artwork">
                {current.artwork && <img src={current.artwork} alt="" className="w-full h-full object-cover" />}
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                  <Radio className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-ochre">
                  <Radio className="w-3 h-3" /> {t("radio_title")}
                </div>
                <div className="text-sm font-semibold truncate" dir="auto" data-testid="mini-player-title">{current.title}</div>
                <div className="text-xs text-sand/70 truncate" dir="auto" data-testid="mini-player-artist">
                  {current.artist} · <Link to={`/?c=${current.country.code}`} className="hover:text-sand hover:underline" data-testid="mini-player-country">{current.country.short_names[lang]}</Link>
                </div>
                <div className="hidden sm:block mt-1"><ListenFull song={current} countryCode={current.country.code} compact /></div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={radio.toggle} aria-label={radio.playing ? t("song_pause") : t("song_play")} className="w-11 h-11 rounded-full bg-ochre hover:bg-ochre/90 text-white flex items-center justify-center" data-testid="mini-player-toggle">
                  {radio.playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ms-0.5" fill="currentColor" />}
                </button>
                <button onClick={() => radio.go(1)} aria-label="next" className="w-9 h-9 rounded-full bg-sand/10 hover:bg-sand/20 flex items-center justify-center" data-testid="mini-player-next">
                  <SkipForward className="w-4 h-4 rtl:rotate-180" />
                </button>
                <ShareTrack track={current} size="sm" />
                <button onClick={radio.stop} aria-label={t("radio_close")} title={t("radio_close")} className="w-9 h-9 rounded-full text-sand/60 hover:text-sand hover:bg-sand/10 flex items-center justify-center" data-testid="mini-player-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
